import React, {
  useState,
  useMemo,
  useEffect,
  useImperativeHandle,
  useActionState,
  useDeferredValue
} from 'react'
import { useSelector } from 'react-redux'
import { Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap'
import {
  IconX,
  IconSearch,
  IconChevronUp,
  IconChevronDown,
  IconDatabase
} from '@tabler/icons-react'
//@ts-ignore
import { Button, Checkbox, InputGroup } from 'dsm-react-lib'
import { useDataboxService } from '../services/databoxService'
import SelectedSourcesPanel from './SelectedSourcesPanel'
import { useThemeManager } from '../../../../_hooks/useThemeManager'

// Get tier color
export const getTierColor = (tier: string) => {
  return tier === 'Business Optimised'
    ? 'bg-[var(--tier-optimised)]'
    : tier === 'Curated'
    ? 'bg-[var(--tier-curated)]'
    : 'bg-[var(--tier-standard)]'
}

// Get tier abbreviation
export const getTierAbbr = (tier: string) => {
  return tier === 'Business Optimised' ? 'OPT' : tier === 'Curated' ? 'CUR' : 'SRC'
}

// Tier badge CSS classes are defined in global.css as .badge-business-optimised, .badge-curated, .badge-standard

interface AddDataProductsModalProps {
  isOpen: boolean
  toggle: () => void
  // Shared tab bar, rendered as the first item of this tab's own left column so the right-hand
  tabsBar?: React.ReactNode
  databoxId?: string
  onDataProductsAdded?: () => void
  existingDataProducts?: any[]
  addDataProductToAgent?: (dataProducts: any) => void
  removeDataProductFromAgent?: (productIds: string[]) => void
  // Renders just the body content (no outer Modal/ModalHeader) for embedding as a tab in DataSourcesModal.tsx
  asTabContent?: boolean
  // Databoxes already selected on the Databoxes tab
  existingDataboxesForPanel?: any[]
  onRemoveDatabox?: (databoxId: string) => void
  // Removes a single file/data product from a databox's narrowed selection (Databoxes tab)
  onRemoveDataboxItem?: (databoxId: string, itemId: string) => void
  lockedDataboxIds?: string[]
  lockedItemIds?: string[]
  // The Personal Databox tab's current (unsaved) selection - at most one databox
  existingPersonalDataboxForPanel?: any[]
  // Whether the agent had ANY databoxes persisted before this modal opened - unlike the live existingDataboxesForPanel/existingPersonalDataboxForPanel, doesn't drop to 0 the instant the last one is unchecked, so the submit button stays enabled to persist that removal.
  hasExistingDataboxes?: boolean
  lockedDataProductIds?: string[]
  // Reports this tab's current (unsaved) selection to the parent modal
  onLiveSelectionChange?: (dataProducts: Array<{ id: string; name: string }>) => void
  // Lets the other tab commit its own pending selection before this tab submits
  onBeforeSubmit?: () => void
  // Reports whether the user has any accessible data products at all
  onDataProductsAvailabilityChange?: (hasDataProducts: boolean) => void
  // Reports whether this tab's own data products list fetch is still in flight
  onLoadingChange?: (isLoading: boolean) => void
  // Loading state of the OTHER two categories (this tab's own Data Products state is computed locally)
  isFetchingPersonal?: boolean
  isFetchingTeam?: boolean
  // True while the agent's existing data sources are still being fetched from the server -
  // shows a loading state in the Selected Sources panel instead of "No sources selected"
  isLoadingExistingSources?: boolean
  // True (default) in the Agent Wizard where Redux's agentReducer.agent is reliable; false in chat's "Add sources" flow where it may hold stale leftover state and would hijack team resolution.
  useAgentWizardTeamContext?: boolean
  // True (Agent Wizard) / passed explicitly (chat flow) when the agent's visibility is Enterprise -
  // restricts the list to Public data products only, matching AddDataboxModal's team restriction.
  isEnterpriseAgent?: boolean
  // Ref exposing this tab's imperative handle to the parent DataSourcesModal
  ref?: React.Ref<AddDataProductsModalHandle>
}

interface DataProduct {
  id: string
  name: string
  description: string
  tier: 'Business Optimised' | 'Curated' | 'Standard'
  accessStatus?: string
  securityClassification?: string
}

// Imperative methods exposed to the parent DataSourcesModal
export interface AddDataProductsModalHandle {
  uncheckDataProduct: (id: string) => void
  // Commits this tab's selection without closing the modal
  commitPendingSelection: () => void
}

const AddDataProductsModal = ({
  isOpen,
  toggle,
  tabsBar,
  databoxId,
  onDataProductsAdded,
  existingDataProducts = [],
  addDataProductToAgent,
  removeDataProductFromAgent,
  asTabContent = false,
  existingDataboxesForPanel = [],
  onRemoveDatabox,
  onRemoveDataboxItem,
  lockedDataboxIds = [],
  lockedItemIds = [],
  existingPersonalDataboxForPanel = [],
  hasExistingDataboxes = false,
  lockedDataProductIds = [],
  onLiveSelectionChange,
  onBeforeSubmit,
  onDataProductsAvailabilityChange,
  onLoadingChange,
  isFetchingPersonal,
  isFetchingTeam,
  isLoadingExistingSources = false,
  useAgentWizardTeamContext = true,
  isEnterpriseAgent = false,
  ref
}: AddDataProductsModalProps) => {
  const {
    isLoadingDataProducts,
    fetchAvailableDataProducts,
    isSubmitting,
    addDataProductsToDatabox
  } = useDataboxService()
  const agent = useSelector((state: any) => state?.agentReducer?.agent)
  // Enterprise agents only ever deal with Public data products
  const isEnterpriseTypeAgent = useAgentWizardTeamContext
    ? agent?.agentType === 'Enterprise'
    : isEnterpriseAgent

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [showAllSelected, setShowAllSelected] = useState(false)
  const [dataProducts, setDataProducts] = useState<DataProduct[]>([])
  const [skipCount] = useState(0)
  const [maxResultCount] = useState(100)
  const { isDarkTheme } = useThemeManager()
  const lockedDataProductIdSet = useMemo(
    () => new Set(lockedDataProductIds.map(String)),
    [lockedDataProductIds]
  )
  const lockedDataboxIdSet = useMemo(
    () => new Set(lockedDataboxIds.map(String)),
    [lockedDataboxIds]
  )
  const lockedItemIdSet = useMemo(() => new Set(lockedItemIds.map(String)), [lockedItemIds])

  // Reports this tab's own fetch state (loading the data products list) to the parent modal, so
  // it can be combined with the other tabs' loading state.
  useEffect(() => {
    onLoadingChange?.(isLoadingDataProducts)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingDataProducts])

  // Fetch data products from API
  useEffect(() => {
    const loadDataProducts = async () => {
      const products = await fetchAvailableDataProducts(skipCount, maxResultCount)
      setDataProducts(products)
      const accessible = isEnterpriseTypeAgent
        ? (products || []).filter(
            (product: DataProduct) => product.securityClassification?.toLowerCase() === 'public'
          )
        : (products || []).filter((product: DataProduct) => product.accessStatus === 'FULL_ACCESS')
      onDataProductsAvailabilityChange?.(accessible.length > 0)
    }

    if (isOpen) {
      loadDataProducts()
      // Pre-select existing data products
      const existingIds = existingDataProducts
        .map((dp: any) => String(dp.dataProductExternalId || dp.id))
        .filter(Boolean)
      setSelectedRows(existingIds)
    }
  }, [isOpen, skipCount, maxResultCount, isEnterpriseTypeAgent])

  // Enterprise agents skip the full-access check entirely and only see Public data products;
  // everyone else only sees data products they have full access to. Then apply search filter.
  const filteredDataProducts = useMemo(() => {
    const accessible = isEnterpriseTypeAgent
      ? dataProducts.filter((product) => product.securityClassification?.toLowerCase() === 'public')
      : dataProducts.filter((product) => product.accessStatus === 'FULL_ACCESS')
    if (!searchTerm) return accessible
    return accessible.filter((product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [searchTerm, dataProducts, isEnterpriseTypeAgent])

  // Get selected data products - falls back to existingDataProducts for ids not present in
  // the fetched `dataProducts` page, so already-associated products don't disappear from the UI
  const getSelectedDataProducts = () => {
    const fetchedMatches = dataProducts.filter((product) => selectedRows.includes(product.id))
    const fetchedIds = new Set(fetchedMatches.map((product) => product.id))

    const fallbackMatches = selectedRows
      .filter((id) => !fetchedIds.has(id))
      .map((id) =>
        existingDataProducts.find(
          (dp: any) => String(dp.dataProductExternalId || dp.id) === String(id)
        )
      )
      .filter(Boolean)
      .map((dp: any) => ({ ...dp, id: String(dp.dataProductExternalId || dp.id) }))

    return [...fetchedMatches, ...fallbackMatches]
  }

  // Report this tab's current selection to the parent, live, for the other tab's panel
  useEffect(() => {
    if (!onLiveSelectionChange) return
    onLiveSelectionChange(getSelectedDataProducts().map((p) => ({ id: p.id, name: p.name })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, dataProducts])

  // Remove selected data product
  const removeSelectedDataProduct = (id: string) => {
    if (lockedDataProductIdSet.has(String(id))) {
      return
    }
    setSelectedRows((prev) => prev.filter((rowId) => rowId !== id))
  }

  // Get CSS class for badge styling
  const getBadgeClass = (tier: string) => {
    return tier === 'Business Optimised'
      ? 'badge-business-optimised'
      : tier === 'Curated'
      ? 'badge-curated'
      : 'badge-standard'
  }

  // Check if product is already in databox
  const isProductInDatabox = (productId: string) => {
    return existingDataProducts.some(
      (dp: any) => String(dp.dataProductExternalId || dp.id) === String(productId)
    )
  }

  // Adds newly checked / removes unchecked products, without closing the modal
  const commitAgentSelection = () => {
    // Get existing product IDs
    const existingIds = existingDataProducts.map((dp: any) =>
      String(dp.dataProductExternalId || dp.id)
    )

    // Find products that were removed (in existing but not in selected)
    const removedIds = existingIds.filter((id: string) => !selectedRows.includes(id))

    // Remove unchecked products
    if (removedIds.length > 0 && removeDataProductFromAgent) {
      removeDataProductFromAgent(removedIds)
    }

    // Add newly selected products
    const newlySelectedIds = selectedRows.filter((id: string) => !existingIds.includes(id))
    if (newlySelectedIds.length > 0) {
      const selectedDataProducts = dataProducts
        ?.filter((dp: any) => newlySelectedIds.includes(dp.id))
        .map((dp: any) => {
          return { ...dp, type: 'dataproduct' }
        })

      if (addDataProductToAgent) {
        addDataProductToAgent(selectedDataProducts)
      }
    }
  }

  useImperativeHandle(ref, () => ({
    uncheckDataProduct: removeSelectedDataProduct,
    commitPendingSelection: commitAgentSelection
  }))

  // Handle adding data products to databox
  const handleAddDataProducts = async () => {
    // No databoxId - used in agent context
    if (!databoxId) {
      onBeforeSubmit?.()
      commitAgentSelection()
      toggle()
      return
    }

    // Databox flow - add to specific databox
    try {
      // Filter out already added products
      const newProductIds = selectedRows.filter((id) => !isProductInDatabox(id))

      if (newProductIds.length === 0) {
        // All selected products are already added
        toggle()
        return
      }

      // Add products using the service
      await addDataProductsToDatabox(databoxId, newProductIds)

      // Clear selections and close modal on success
      setSelectedRows([])

      // Notify parent component to refresh the list
      if (onDataProductsAdded) {
        onDataProductsAdded()
      }

      toggle()
    } catch (error) {
      console.error('Error adding data products:', error)
    }
  }

  // Read-only summary of databoxes selected on the other tabs (Team Databoxes + Personal
  // Databox), reshaped for SelectedSourcesPanel.
  const nonEmptyPersonalDataboxForPanel = existingPersonalDataboxForPanel.filter(
    (db: any) => (db?.selectedItems?.length ?? 0) > 0
  )
  const allPanelSourceDataboxes = [...existingDataboxesForPanel, ...nonEmptyPersonalDataboxForPanel]
  const panelDataboxes = allPanelSourceDataboxes.map((db: any) => ({
    id: db.id,
    smartDocsDataboxId: db.smartDocsDataboxId || db.id,
    name: db.name,
    databoxDocumentsCount: db.databoxDocumentsCount ?? db.filesCount ?? 0,
    databoxDataProductsCount: db.databoxDataProductsCount ?? db.dataProductsCount ?? 0
  }))
  const panelDataboxSelectedRows: string[] = []
  const panelDataboxSelectedDocumentsByDatabox: Record<string, { name: string; items: any[] }> = {}
  allPanelSourceDataboxes.forEach((db: any) => {
    // Same id preference (smartDocsDataboxId over id) as AddDataboxModal uses internally, so remove/remove-all actually match its selectedRows.
    const key = db.smartDocsDataboxId || db.id
    if (db.selectedItems?.length > 0) {
      panelDataboxSelectedDocumentsByDatabox[key] = { name: db.name, items: db.selectedItems }
    } else {
      panelDataboxSelectedRows.push(key)
    }
  })

  const listContent = (
    <>
      {/* Search */}
      <div className="mb-1">
        <InputGroup
          addon={<IconSearch size={14} />}
          addonPosition="start"
          addonType="merged"
          isDarkMode={isDarkTheme}
          className="search-input theme-input-group w-full"
          placeholder="Search for data products"
          value={searchTerm}
          onChange={(e: any) => setSearchTerm(e.target.value)}
          data-testid="personal-databox-search"
        />
      </div>

      {/* Data Products List */}
      <div className="flex-1 overflow-y-auto mb-1 max-h-[500px]">
        {isLoadingDataProducts ? (
          <div className="flex items-center justify-center py-4">
            <Spinner color="primary" />
          </div>
        ) : filteredDataProducts.length === 0 ? (
          <div className="flex items-center justify-center py-4">
            <div className="theme-text-subheading">No data products found</div>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredDataProducts.map((product) => {
              const isSelected = selectedRows.includes(product.id)
              const isLocked = lockedDataProductIdSet.has(String(product.id))
              const toggleSelection = () => {
                if (isLocked) return
                setSelectedRows(
                  isSelected
                    ? selectedRows.filter((id) => id !== product.id)
                    : [...selectedRows, product.id]
                )
              }
              return (
                <div
                  key={product.id}
                  className={`flex items-start gap-1 py-1 px-1 rounded ${
                    isLocked
                      ? 'cursor-not-allowed opacity-60'
                      : 'cursor-pointer theme-hover-surface'
                  }`}
                  onClick={toggleSelection}>
                  <Checkbox
                    className="!mr-0 self-center"
                    variant="custom"
                    checked={isSelected}
                    disabled={isLocked}
                    onChange={toggleSelection}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                  <div
                    className={`w-10 h-10 ${getTierColor(
                      product.tier
                    )} text-white text-sm font-bold rounded flex items-center justify-center flex-shrink-0`}>
                    {getTierAbbr(product.tier)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5
                      className="font-bold theme-text-heading line-clamp-1 mb-0"
                      title={product.name}>
                      {product.name}
                    </h5>
                    <p
                      className="text-sm theme-text-body line-clamp-1 !mb-0"
                      title={product.description}>
                      {product.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Selected Items Display */}
      {!asTabContent && selectedRows.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium theme-text-body">
              {selectedRows.length} data products selected
            </span>
            {selectedRows.length > 4 && (
              <Button
                variant="flat"
                className="p-50"
                label={showAllSelected ? 'View less' : 'View all'}
                icon={showAllSelected ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                onClick={() => setShowAllSelected(!showAllSelected)}
              />
            )}
          </div>
          <hr className="theme-border-default mb-1" />
          <div className="flex flex-wrap gap-1 max-h-[20%] overflow-y-auto">
            {getSelectedDataProducts()
              .slice(0, showAllSelected ? undefined : 4)
              .map((product) => {
                const isLocked = lockedDataProductIdSet.has(String(product.id))
                return (
                  <div
                    key={product.id}
                    className={`flex items-center gap-50 px-1 py-25 rounded-full text-sm cursor-pointer hover:shadow-sm transition-all border ${getBadgeClass(
                      product.tier
                    )}`}>
                    <span>{product.name} </span>
                    <div
                      onClick={() => !isLocked && removeSelectedDataProduct(product.id)}
                      className={`rounded-full p-0.5 transition-colors ${
                        isLocked
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer hover:bg-opacity-20'
                      }`}>
                      <IconX size={14} />
                    </div>
                  </div>
                )
              })}

            {!showAllSelected && selectedRows.length > 4 && (
              <div className="px-1 py-25 theme-surface-muted rounded-full text-sm theme-text-body">
                +{selectedRows.length - 4}
              </div>
            )}
          </div>
          <hr className="theme-border-default mb-1" />
        </>
      )}
    </>
  )

  const bodyContent = (
    <ModalBody className="theme-surface p-1 flex-1 overflow-hidden flex flex-col">
      {asTabContent ? (
        <div className="flex flex-1 overflow-hidden">
          <div className="flex flex-col flex-1 overflow-hidden pr-2">
            {tabsBar}
            {listContent}
          </div>
          <div className="flex flex-col overflow-auto pl-3 ml-1 border-l theme-border-default w-[35%] max-h-[calc(100vh-200px)]">
            <SelectedSourcesPanel
              selectedRows={panelDataboxSelectedRows}
              selectedDocumentsByDatabox={panelDataboxSelectedDocumentsByDatabox}
              isFetchingPersonal={isFetchingPersonal}
              isFetchingTeam={isFetchingTeam}
              isFetchingDataProducts={isLoadingExistingSources || isLoadingDataProducts}
              databoxes={panelDataboxes}
              databoxDetailsCache={{}}
              personalDataboxId={nonEmptyPersonalDataboxForPanel[0]?.id}
              onRemoveDatabox={onRemoveDatabox}
              onRemoveItem={onRemoveDataboxItem}
              lockedDataboxIds={Array.from(lockedDataboxIdSet)}
              lockedItemIds={Array.from(lockedItemIdSet)}
              standaloneDataProducts={getSelectedDataProducts().map((p) => ({
                id: p.id,
                name: p.name
              }))}
              onRemoveDataProduct={removeSelectedDataProduct}
              lockedDataProductIds={Array.from(lockedDataProductIdSet)}
            />
          </div>
        </div>
      ) : (
        listContent
      )}

      {/* Footer */}
      <div className="flex items-center justify-end mt-2">
        <div className="flex gap-1">
          <Button
            label="Cancel"
            variant="outline"
            className="theme-neutral-outline-btn"
            onClick={toggle}
            disabled={isSubmitting}
          />
          <Button
            icon={isSubmitting && <Spinner size="sm" className="mr-1" />}
            label={
              isSubmitting
                ? 'Updating...'
                : existingDataProducts.length === 0 &&
                  existingDataboxesForPanel.length === 0 &&
                  !hasExistingDataboxes
                ? 'Add sources'
                : 'Update sources'
            }
            color="primary"
            disabled={
              isSubmitting ||
              (selectedRows.length === 0 &&
                existingDataboxesForPanel.length === 0 &&
                !hasExistingDataboxes &&
                existingDataProducts.length === 0)
            }
            onClick={handleAddDataProducts}
          />
        </div>
      </div>
    </ModalBody>
  )

  if (asTabContent) {
    return bodyContent
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      contentClassName="min-h-[600px] h-[90%] max-w-[90vw] mx-auto">
      <ModalHeader
        className={`!justify-between !border-0 !flex !items-center chat-primary theme-bg flex-shrink-0`}
        toggle={toggle}
        close={
          <IconX
            size={24}
            onClick={toggle}
            className="text-white transition-opacity duration-300 ease-in-out hover:opacity-80 cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center gap-1">
          <IconDatabase size={20} className="text-white" />
          <span className="text-white">Add data products</span>
        </div>
      </ModalHeader>
      {bodyContent}
    </Modal>
  )
}

AddDataProductsModal.displayName = 'AddDataProductsModal'

export default AddDataProductsModal
